import React from 'react';
import { Cloud, RefreshCw, AlertTriangle, Info } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// Valódi Cloudflare-forgalom (GraphQL Analytics, /api/system/cf-usage)
// A keret fiókszintű: a DEV és a LIVE ugyanabból fogy. Napi nullázás: 00:00 UTC.
// ═══════════════════════════════════════════════════════════════

export interface CfUsage {
  configured: boolean;
  fetched_at?: string;
  cached?: boolean;
  day?: string;
  month?: string;
  limits?: Record<string, number>;
  errors?: { part: string; message: string }[];
  workers?: { requests_today: number; errors_today: number; percent: number; by_script: { name: string; requests: number; errors: number }[] } | null;
  d1?: { rows_read_today: number; rows_written_today: number; read_percent: number; write_percent: number; by_database: { id: string; rows_read: number; rows_written: number; own?: boolean }[] } | null;
  r2?: { class_a_month: number; class_b_month: number; class_a_percent: number; class_b_percent: number; by_bucket: { name: string; class_a: number; class_b: number }[] } | null;
}

const fmt = (n?: number) => (n ?? 0).toLocaleString('hu-HU');

export const levelOf = (percent?: number | null) =>
  percent === null || percent === undefined ? 'unknown' : percent >= 100 ? 'over' : percent >= 80 ? 'warn' : 'ok';

const barColor = (p: number) => (p >= 100 ? 'bg-rose-500' : p >= 80 ? 'bg-amber-500' : 'bg-emerald-500');

const Meter: React.FC<{ label: string; used?: number; max?: number; percent?: number; hint: string }> = ({ label, used, max, percent, hint }) => {
  const p = percent ?? 0;
  return (
    <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-700 dark:text-slate-200">{label}</span>
        <span className={`font-bold ${p >= 100 ? 'text-rose-600' : p >= 80 ? 'text-amber-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{p}%</span>
      </div>
      <div className="text-lg font-extrabold text-slate-900 dark:text-white">
        {fmt(used)} <span className="text-xs font-normal text-slate-500">/ {fmt(max)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden" role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className={`h-full ${barColor(p)}`} style={{ width: `${Math.min(100, p)}%` }} />
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400">{hint}</div>
    </div>
  );
};

export const CloudflareUsagePanel: React.FC<{ usage: CfUsage | null; loading: boolean; onRefresh: () => void }> = ({ usage, loading, onRefresh }) => {
  if (!usage) return null;

  if (!usage.configured) {
    return (
      <div className="p-4 rounded-2xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs text-sky-900 dark:text-sky-200 space-y-2">
        <div className="font-bold flex items-center gap-1.5"><Info className="w-4 h-4" /> A valódi Cloudflare-forgalom mérése nincs beállítva</div>
        <p>
          A fenti csempék az alkalmazás saját számlálói. A Cloudflare ingyenes keretét leginkább fogyasztó Worker-kéréseket és
          D1-sorolvasásokat csak a Cloudflare méri. Bekapcsolás: hozz létre egy API-tokent <strong>Account → Account Analytics → Read</strong> joggal,
          majd ennél a környezetnél:
        </p>
        <pre className="text-[11px] bg-white/70 dark:bg-slate-900/70 border border-sky-200 dark:border-sky-900 rounded-lg p-2 overflow-x-auto">{`npx wrangler secret put CF_ANALYTICS_TOKEN --config <környezet>.toml
npx wrangler secret put CF_ACCOUNT_ID --config <környezet>.toml`}</pre>
      </div>
    );
  }

  const l = usage.limits || {};
  const time = usage.fetched_at ? new Date(usage.fetched_at).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="p-5 rounded-2xl bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-xs space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h3 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
            <Cloud className="w-4 h-4 text-orange-500" /> Cloudflare-forgalom – a teljes fiók (DEV + LIVE együtt)
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Napi keret: {usage.day} (00:00 UTC-től) · R2 havi: {usage.month} · frissítve {time}{usage.cached ? ' (5 perces gyorsítótárból)' : ''}
          </p>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Lekérés most
        </button>
      </div>

      {(usage.errors || []).length > 0 && (
        <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-[11px] text-amber-900 dark:text-amber-200 flex gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            Néhány adat nem kérhető le: {(usage.errors || []).map((e) => `${e.part}: ${e.message}`).join(' · ')}
            <div className="mt-0.5">Ellenőrizd, hogy a token „Account Analytics: Read” jogú-e, és a CF_ACCOUNT_ID helyes-e.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {usage.workers && <Meter label="Worker-kérések ma" used={usage.workers.requests_today} max={l.workersRequestsPerDay} percent={usage.workers.percent} hint={`Minden Worker és Pages Function együtt · ${fmt(usage.workers.errors_today)} hiba`} />}
        {usage.d1 && <Meter label="D1 olvasott sorok ma" used={usage.d1.rows_read_today} max={l.d1RowsReadPerDay} percent={usage.d1.read_percent} hint="Minden adatbázis együtt" />}
        {usage.d1 && <Meter label="D1 írt sorok ma" used={usage.d1.rows_written_today} max={l.d1RowsWrittenPerDay} percent={usage.d1.write_percent} hint="Minden adatbázis együtt" />}
        {usage.r2 && <Meter label="R2 írás (Class A) e hónapban" used={usage.r2.class_a_month} max={l.r2ClassAPerMonth} percent={usage.r2.class_a_percent} hint={`Olvasás (Class B): ${fmt(usage.r2.class_b_month)} / ${fmt(l.r2ClassBPerMonth)} (${usage.r2.class_b_percent}%)`} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-[11px]">
        {usage.workers && usage.workers.by_script.length > 0 && (
          <div className="space-y-1">
            <div className="font-semibold text-slate-600 dark:text-slate-300">Kérések Workerenként (ma)</div>
            {usage.workers.by_script.slice(0, 6).map((s) => (
              <div key={s.name} className="flex justify-between gap-2 text-slate-600 dark:text-slate-400"><span className="truncate font-mono">{s.name}</span><span>{fmt(s.requests)}</span></div>
            ))}
          </div>
        )}
        {usage.d1 && usage.d1.by_database.length > 0 && (
          <div className="space-y-1">
            <div className="font-semibold text-slate-600 dark:text-slate-300">D1 adatbázisonként (ma, olvasott / írt sor)</div>
            {usage.d1.by_database.slice(0, 6).map((d) => (
              <div key={d.id} className="flex justify-between gap-2 text-slate-600 dark:text-slate-400">
                <span className="truncate font-mono">{d.own ? 'ez a környezet' : `${d.id.slice(0, 8)}…`}</span>
                <span>{fmt(d.rows_read)} / {fmt(d.rows_written)}</span>
              </div>
            ))}
          </div>
        )}
        {usage.r2 && usage.r2.by_bucket.length > 0 && (
          <div className="space-y-1">
            <div className="font-semibold text-slate-600 dark:text-slate-300">R2 tárolónként (e hónapban, A / B)</div>
            {usage.r2.by_bucket.slice(0, 6).map((b) => (
              <div key={b.name} className="flex justify-between gap-2 text-slate-600 dark:text-slate-400"><span className="truncate font-mono">{b.name}</span><span>{fmt(b.class_a)} / {fmt(b.class_b)}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CloudflareUsagePanel;
